import { Switch } from '@mantine/core';
import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { type LedgerChanged, LedgerRow } from './LedgerRow';
import classes from './SwitchRow.module.css';

export interface SwitchRowProps {
  label: ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Defaults to "On". */
  onLabel?: string;
  /** Defaults to "Off". */
  offLabel?: string;
  help?: ReactNode;
  changed?: LedgerChanged;
  /** Inline text after the state word, e.g. the flagged count. */
  extra?: ReactNode;
  /** Same as LedgerRow: a red line for conditions that reject, drop or fail to deliver. */
  risk?: ReactNode;
  /** Controls on the right, e.g. Deliveries and Unlink on an integration row. */
  action?: ReactNode;
  disabled?: boolean;
  id?: string;
}

export function SwitchRow({
  label,
  checked,
  onChange,
  onLabel,
  offLabel,
  help,
  changed,
  extra,
  risk,
  action,
  disabled,
  id,
}: SwitchRowProps) {
  const { t } = useTranslation();
  const labelId = useId();

  return (
    <LedgerRow
      id={id}
      label={<span id={labelId}>{label}</span>}
      value={
        <span className={classes.line}>
          <Switch
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange(e.currentTarget.checked)}
            aria-labelledby={labelId}
          />
          <strong>{checked ? (onLabel ?? t('ui.on')) : (offLabel ?? t('ui.off'))}</strong>
          {extra}
        </span>
      }
      help={help}
      changed={changed}
      risk={risk}
      action={action}
    />
  );
}
